import React from 'react';

interface JobNoteFormProps {
  onSubmit: (note: string, noteType: string, isPinned: boolean) => Promise<void>;
  onCancel?: () => void;
}

export const JobNoteForm: React.FC<JobNoteFormProps> = ({ onSubmit, onCancel }) => {
  const [note, setNote] = React.useState('');
  const [noteType, setNoteType] = React.useState<'general' | 'important' | 'issue' | 'resolved'>('general');
  const [isPinned, setIsPinned] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!note.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(note, noteType, isPinned);
      // Reset form
      setNote('');
      setNoteType('general');
      setIsPinned(false);
    } catch (error) {
      console.error('Failed to submit note:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-[#1a2332] rounded-lg border border-gray-700 p-4">
      <div className="space-y-3">
        {/* Note Input */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Add Note
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Write a note about this job..."
            className="w-full px-4 py-2.5 rounded-lg bg-[#101922] text-gray-100 border border-gray-700 hover:border-gray-600 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none placeholder:text-gray-500"
            rows={3}
            disabled={isSubmitting}
          />
        </div>

        {/* Options Row */}
        <div className="flex items-center gap-4 flex-wrap">
          {/* Note Type */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-400">Type:</label>
            <select
              value={noteType}
              onChange={(e) => setNoteType(e.target.value as any)}
              className="px-3 py-1.5 rounded-lg bg-[#101922] text-gray-100 border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
              disabled={isSubmitting}
            >
              <option value="general">General</option>
              <option value="important">Important</option>
              <option value="issue">Issue</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>

          {/* Pin Checkbox */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isPinned}
              onChange={(e) => setIsPinned(e.target.checked)}
              className="w-4 h-4 rounded bg-[#101922] border-gray-700 text-blue-600 focus:ring-2 focus:ring-blue-500/20"
              disabled={isSubmitting}
            />
            <span className="text-sm text-gray-400">Pin this note</span>
          </label>

          {/* Submit Button */}
          <div className="flex gap-2 ml-auto">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={isSubmitting}
                className="px-4 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={!note.trim() || isSubmitting}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <span className="material-symbols-outlined animate-spin text-sm">
                    progress_activity
                  </span>
                  Adding...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-sm">add</span>
                  Add Note
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

